/**
 * @fileoverview Componente responsável por estruturar o layout autenticado, sidebar, navegação mobile e listeners globais.
 */

import React, { useState, useContext, useEffect } from "react";
import {
  makeStyles,
  Drawer,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";

import MainListItems from "./MainListItems";
import UserModal from "../components/UserModal";
import BottomNavigation from "../components/BottomNavigation";
import FaviconUnreadBadge from "../components/FaviconUnreadBadge";
import AiApprovalToastListener from "../components/AiApprovalToastListener";
import ImpersonationBanner from "../components/ImpersonationBanner";
import OnboardingModal from "../components/Onboarding/OnboardingModal";
import OnboardingLauncher from "../components/Onboarding/OnboardingLauncher";
import TourSpotlight from "../components/Onboarding/TourSpotlight";
import Celebration from "../components/Onboarding/Celebration";
import BlockedModal from "../components/Onboarding/BlockedModal";
import TrialCountdownBanner from "../components/Onboarding/TrialCountdownBanner";
import PlanCheckoutModal from "../components/Onboarding/PlanCheckoutModal";
import { useOnboarding } from "../context/Onboarding/OnboardingContext";
import useInternalChatNotifications from "../hooks/useInternalChatNotifications";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";

import { SocketContext } from "../context/Socket/SocketContext";

import toastError from "../errors/toastError";

import ColorModeContext from "./themeContext";

const drawerWidth = 72; // Sidebar compacta

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    backgroundColor: theme.palette.fancyBackground,
    '& .MuiButton-outlinedPrimary': {
      color: theme.mode === 'light' ? '#FFF' : '#FFF',
      backgroundColor: theme.mode === 'light' ? theme.palette.primary.main : '#1A1A1A',
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      color: theme.mode === 'light' ? 'Primary' : '#88898D',
    }
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    background: theme.palette.barraSuperior,
    borderRight: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      display: "none", // Esconder drawer no mobile
    },
    overflowX: "hidden",
    ...theme.scrollbarStylesSoft
  },
  content: {
    flex: 1,
    overflow: "hidden",
    height: "auto",
    display: "flex",
    flexDirection: "column",
    position: "relative",
  },
  menuList: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const { user } = useContext(AuthContext);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [volume, setVolume] = useState(() => {
    const storedVolume = parseFloat(localStorage.getItem("volume"));
    return Number.isNaN(storedVolume) ? 1 : storedVolume;
  });

  const socketManager = useContext(SocketContext);

  // O OnboardingProvider vive no topo do App (fora do Router) e busca o
  // progresso só no 1º mount. Após login (history.push, SPA, sem reload) ele
  // não re-buscaria — então forçamos um refetch quando o layout autenticado
  // monta (e quando a empresa ativa muda), garantindo o modal em conta nova.
  const { refetch: refetchOnboarding } = useOnboarding();
  useEffect(() => {
    refetchOnboarding();
  }, [refetchOnboarding, user?.companyId]);

  // Notificações nativas do browser para DMs e menções do Chat Interno.
  // O hook é global (vive no layout) pra funcionar mesmo quando o user não
  // está em /internal-chat. Canais comuns não disparam popup — só badge.
  useInternalChatNotifications();

  // States for BottomNavigation (mobile)
  const [connectionWarning, setConnectionWarning] = useState(false);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId");

    const socket = socketManager.getSocket(companyId);

    const handleAuth = (data) => {
      if (data.user.id === +userId) {
        toastError("Sua conta foi acessada em outro computador.");
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    };

    socket.on(`company-${companyId}-auth`, handleAuth);

    socket.emit("userStatus");
    const interval = setInterval(() => {
      socket.emit("userStatus");
    }, 1000 * 60 * 5);

    return () => {
      socket.off(`company-${companyId}-auth`, handleAuth);
      clearInterval(interval);
    };
  }, [socketManager]);

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
  };

  const handleClickLogout = () => {
    handleLogout();
  };

  const drawerClose = () => {
    // No-op - drawer is always collapsed
  };

  const toggleColorMode = () => {
    colorMode.toggleColorMode();
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <FaviconUnreadBadge />
      <AiApprovalToastListener />

      {/* Onboarding guiado — só para admin/criador da conta; não bloqueia o uso. */}
      {(user?.profile === "admin" || user?.super) && (
        <>
          <OnboardingModal />
          <OnboardingLauncher />
          <TourSpotlight />
          <Celebration />
          {/* Checkout self-service no fim do trial — só o admin compra. */}
          <PlanCheckoutModal />
        </>
      )}

      {/* Billing: bloqueio/pausa aplica-se à empresa toda (não só admin). O
          admin pausado paga no PlanCheckoutModal; o não-admin vê este aviso. */}
      <BlockedModal isAdmin={user?.profile === "admin" || user?.super} />

      {/* Desktop Sidebar */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          <div className={classes.menuList}>
            <MainListItems
              drawerClose={drawerClose}
              collapsed={true}
              user={user}
              volume={volume}
              setVolume={setVolume}
              toggleColorMode={toggleColorMode}
              onOpenProfile={handleOpenUserModal}
              onLogout={handleClickLogout}
            />
          </div>
        </Drawer>
      )}

      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />

      <main className={classes.content}>
        <ImpersonationBanner />
        <TrialCountdownBanner />
        {children ? children : null}
      </main>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <BottomNavigation
          user={user}
          volume={volume}
          setVolume={setVolume}
          toggleColorMode={toggleColorMode}
          onOpenProfile={handleOpenUserModal}
          onLogout={handleClickLogout}
          connectionWarning={connectionWarning}
        />
      )}
    </div>
  );
};

export default LoggedInLayout;
